import React, { useState, useEffect, useCallback } from 'react';
import api, { p2pAPI } from '../../services/api';

const METHOD_LABELS = { zelle: 'Zelle', usdt_trc20: 'USDT (TRC-20)', stripe: 'Tarjeta' };
const ALL_METHODS = ['zelle', 'usdt_trc20'];
const CURRENCIES = ['USD', 'EUR', 'USDT'];
const PAYOUTS = [['cash', '💵 Efectivo'], ['transfermovil', '📱 Transfermóvil'], ['cubacel', '📶 Cubacel']];

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('es', { maximumFractionDigits: 2 });
}

// ── Luz online reutilizable ───────────────────────────────────────────
export function OnlineDot({ online, size = 10 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: size, height: size, borderRadius: '50%', background: online ? '#2d6a4f' : '#c94a1e', boxShadow: online ? '0 0 0 3px rgba(45,106,79,.18)' : 'none' }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: online ? '#2d6a4f' : '#c94a1e' }}>{online ? 'En línea' : 'Desconectado'}</span>
    </span>
  );
}

// ── Form de campos de anuncio (compartido por crear y editar) ─────────
function OfferForm({ initial, onSubmit, submitLabel, onCancel }) {
  const [f, setF] = useState(initial);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mkt, setMkt] = useState(null);   // { rate, at } para pay_currency

  const fetchMarket = async () => {
    try {
      const r = await fetch('https://api.guarapo.app/api/v1/rates/');
      const d = await r.json();
      const key = { USD: 'usd_cup', EUR: 'eur_cup', USDT: 'usdt_cup' }[f.pay_currency] || 'usd_cup';
      if (d[key]) {
        setF(prev => ({ ...prev, fixed_rate: String(d[key]) }));
        setMkt({ rate: d[key], at: d.updated_at });
      }
    } catch { setMkt(null); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleMethod = (m) => {
    const has = f.payment_methods.includes(m);
    setF({ ...f, payment_methods: has ? f.payment_methods.filter((x) => x !== m) : [...f.payment_methods, m] });
  };

  const submit = async () => {
    setError('');
    if (f.payment_methods.length === 0) { setError('Elegí al menos un método de pago'); return; }
    if (parseFloat(f.max_amount) < parseFloat(f.min_amount)) { setError('El máximo debe ser ≥ mínimo'); return; }
    if (f.pricing_mode === 'FIXED' && !f.fixed_rate) { setError('Tasa fija requiere un valor'); return; }
    setBusy(true);
    const ok = await onSubmit(f);
    setBusy(false);
    if (!ok?.error) return;
    setError(ok.error);
  };

  return (
    <div>
      <label style={lbl}>Moneda de precio</label>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {CURRENCIES.map((c) => <button key={c} onClick={() => setF({ ...f, pay_currency: c })} style={chip(f.pay_currency === c)}>{c}</button>)}
      </div>

      <label style={lbl}>Entrega en Cuba</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {PAYOUTS.map(([v, l]) => <button key={v} onClick={() => setF({ ...f, payout_method: v })} style={chip(f.payout_method === v)}>{l}</button>)}
      </div>

      <label style={lbl}>Métodos de pago que aceptás</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {ALL_METHODS.map((m) => <button key={m} onClick={() => toggleMethod(m)} style={chip(f.payment_methods.includes(m))}>{METHOD_LABELS[m]}</button>)}
      </div>

      <label style={lbl}>Tasa</label>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button onClick={() => setF({ ...f, pricing_mode: 'ANCHORED' })} style={chip(f.pricing_mode === 'ANCHORED')}>Anclada a elToque</button>
        <button onClick={() => setF({ ...f, pricing_mode: 'FIXED' })} style={chip(f.pricing_mode === 'FIXED')}>Fija</button>
      </div>
      {f.pricing_mode === 'ANCHORED' ? (
        <>
          <label style={lbl}>Spread % (negativo = por debajo de elToque)</label>
          <input style={inp} type="number" step="0.1" value={f.spread_pct} onChange={set('spread_pct')} placeholder="-2" />
        </>
      ) : (
        <>
          <label style={lbl}>Tasa fija (CUP por 1 {f.pay_currency})</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...inp, flex: 1 }} type="number" value={f.fixed_rate || ''} onChange={set('fixed_rate')} placeholder="680" />
            <button type="button" onClick={fetchMarket} style={{ padding: '0 14px', borderRadius: 8, border: '1px solid #4ade80', background: 'rgba(74,222,128,.1)', color: '#15803d', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ↻ Tasa de mercado
            </button>
          </div>
          {mkt && <div style={{ fontSize: 11, color: '#7a7060', marginTop: 5 }}>Mercado: {mkt.rate} CUP por {f.pay_currency}. La tasa queda fija; vuelve a pulsar para refrescarla.</div>}
        </>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Mínimo</label>
          <input style={inp} type="number" value={f.min_amount} onChange={set('min_amount')} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Máximo</label>
          <input style={inp} type="number" value={f.max_amount} onChange={set('max_amount')} />
        </div>
      </div>
      <label style={lbl}>Liquidez disponible ({f.pay_currency})</label>
      <input style={inp} type="number" value={f.available_amount} onChange={set('available_amount')} />

      {error && <div style={{ color: '#9b2c5e', fontSize: 13, margin: '6px 0' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button onClick={submit} disabled={busy} style={{ ...btn('#2d6a4f'), flex: 1 }}>{busy ? '…' : submitLabel}</button>
        {onCancel && <button onClick={onCancel} style={{ ...btn('#fff'), color: '#4a4035', border: '1px solid #e2dbd0', flex: '0 0 auto', padding: '13px 18px' }}>Cancelar</button>}
      </div>
    </div>
  );
}

const EMPTY = { pay_currency: 'USD', payout_method: 'cash', payment_methods: ['zelle'], pricing_mode: 'ANCHORED', spread_pct: '-2', fixed_rate: '', min_amount: '20', max_amount: '500', available_amount: '1000' };

// ── Tab principal ─────────────────────────────────────────────────────
export default function AgentOffers({ isOnline, onToggleOnline }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const res = await p2pAPI.myOffers().catch(() => ({ data: [] }));
    setOffers(Array.isArray(res.data) ? res.data : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const numeric = (f) => ({
    pay_currency: f.pay_currency, payout_method: f.payout_method, payment_methods: f.payment_methods,
    pricing_mode: f.pricing_mode, spread_pct: parseFloat(f.spread_pct) || 0,
    fixed_rate: f.pricing_mode === 'FIXED' ? parseFloat(f.fixed_rate) : null,
    min_amount: parseFloat(f.min_amount), max_amount: parseFloat(f.max_amount),
    available_amount: parseFloat(f.available_amount),
  });

  const create = async (f) => {
    const res = await p2pAPI.createOffer(numeric(f)).catch((e) => ({ error: e.response?.data?.detail || 'No se pudo publicar' }));
    if (res?.error) return res;
    setShowForm(false); load(); return {};
  };

  const saveEdit = async (f) => {
    const res = await p2pAPI.updateOffer(editing.id, numeric(f)).catch((e) => ({ error: e.response?.data?.detail || 'No se pudo guardar' }));
    if (res?.error) return res;
    setEditing(null); load(); return {};
  };

  const toggleStatus = async (o) => {
    const next = o.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    await p2pAPI.updateOffer(o.id, { status: next }).catch(() => {});
    load();
  };

  return (
    <div>
      {/* Estado + publicar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #e2dbd0', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
        <div>
          <OnlineDot online={isOnline} />
          <div style={{ fontSize: 11, color: '#7a7060', marginTop: 4 }}>Solo aparecés en el mercado si estás en línea</div>
        </div>
        <button onClick={onToggleOnline} style={chip(isOnline)}>{isOnline ? 'Desconectar' : 'Conectar'}</button>
      </div>

      {!showForm && !editing && (
        <button onClick={() => setShowForm(true)} style={{ ...btn('#c94a1e'), width: '100%', marginBottom: 14 }}>＋ Publicar anuncio</button>
      )}

      {showForm && (
        <div style={{ background: '#f9f6f1', border: '1px solid #e2dbd0', borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <h3 style={{ margin: '0 0 12px', fontFamily: 'Georgia,serif' }}>Nuevo anuncio</h3>
          <OfferForm initial={EMPTY} onSubmit={create} submitLabel="Publicar" onCancel={() => setShowForm(false)} />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#7a7060', padding: 30 }}>Cargando…</div>
      ) : offers.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#7a7060', padding: 30 }}>Todavía no publicaste anuncios.</div>
      ) : (
        offers.map((o) => (
          <div key={o.id} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 12, padding: '14px 16px', marginBottom: 10, opacity: o.status === 'ACTIVE' ? 1 : 0.6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700 }}>{o.pay_currency} → CUP · {o.payout_method}</div>
                <div style={{ fontSize: 12, color: '#7a7060', marginTop: 2 }}>
                  {o.pricing_mode === 'ANCHORED' ? `elToque ${o.spread_pct > 0 ? '+' : ''}${o.spread_pct}%` : `Fija ${fmt(o.fixed_rate)}`} · ${o.min_amount}–{o.max_amount} · liq ${fmt(o.available_amount)}
                </div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                  {o.payment_methods.map((m) => <span key={m} style={{ padding: '2px 8px', borderRadius: 20, background: '#f0ebe2', fontSize: 11, fontWeight: 600 }}>{METHOD_LABELS[m] || m}</span>)}
                </div>
              </div>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: o.status === 'ACTIVE' ? '#eaf4ef' : '#f0ebe2', color: o.status === 'ACTIVE' ? '#2d6a4f' : '#7a7060' }}>{o.status}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => toggleStatus(o)} style={{ ...btn('#fff'), color: '#4a4035', border: '1px solid #e2dbd0', flex: 1, padding: '9px' }}>{o.status === 'ACTIVE' ? 'Pausar' : 'Reactivar'}</button>
              <button onClick={() => setEditing(o)} style={{ ...btn('#fff'), color: '#c94a1e', border: '1px solid #e2dbd0', flex: 1, padding: '9px' }}>Editar</button>
            </div>
          </div>
        ))
      )}

      {/* Modal edición */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }} onClick={() => setEditing(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 20, maxWidth: 440, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontFamily: 'Georgia,serif' }}>Editar anuncio</h3>
            <OfferForm
              initial={{ ...editing, spread_pct: String(editing.spread_pct), fixed_rate: editing.fixed_rate ? String(editing.fixed_rate) : '', min_amount: String(editing.min_amount), max_amount: String(editing.max_amount), available_amount: String(editing.available_amount) }}
              onSubmit={saveEdit} submitLabel="Guardar" onCancel={() => setEditing(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

const inp = { width: '100%', padding: '11px 13px', borderRadius: 9, border: '1px solid #e2dbd0', fontSize: 14, marginBottom: 10, boxSizing: 'border-box', background: '#fff' };
const lbl = { display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 };
const chip = (on) => ({ padding: '7px 12px', borderRadius: 20, border: `1px solid ${on ? '#c94a1e' : '#e2dbd0'}`, background: on ? '#c94a1e' : '#fff', color: on ? '#fff' : '#4a4035', fontSize: 12, fontWeight: 600, cursor: 'pointer' });
const btn = (bg) => ({ padding: '13px', borderRadius: 10, border: 'none', background: bg, color: bg === '#fff' ? '#4a4035' : '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' });
